import {
  computeHealth,
  daysSince,
  healthBand,
  isStalled,
  missingFields,
  type MissingField,
  type PipelineAccount,
  type StageKey,
} from '@/lib/pipelineAdapter';

const STAGE_WEIGHT: Record<StageKey, number> = {
  discovery: 0.15,
  evaluation: 0.35,
  negotiation: 0.65,
  closing: 0.85,
};

const STAGE_ORDER: StageKey[] = ['discovery', 'evaluation', 'negotiation', 'closing'];

const MISSING_FIELD_ORDER: MissingField[] = [
  'EB',
  'Champion',
  'Launch date',
  'Budget',
  'Legal',
  'Procurement',
];

export type ActivityBucket = 'active' | 'cooling' | 'stalled' | 'never';
export type HealthBucketKey = 'at-risk' | 'watch' | 'healthy';

export interface AggregateSlice {
  count: number;
  totalArr: number;
  accountIds: string[];
}

export interface DashboardKpis {
  totalDeals: number;
  totalArr: number;
  atRiskCount: number;
  stalledCount: number;
  weightedArr: number;
  allAccountIds: string[];
  atRiskAccountIds: string[];
  stalledAccountIds: string[];
}

export interface FunnelStage extends AggregateSlice {
  stage: StageKey;
}

export interface OwnerRow {
  ownerKey: string;
  name: string;
  initials: string;
  color: string;
  email: string | null;
  count: number;
  totalArr: number;
  avgHealth: number;
  atRisk: number;
  accountIds: string[];
}

export interface HealthBucket extends AggregateSlice {
  band: HealthBucketKey;
  label: string;
  color: string;
  bg: string;
}

export interface BlockerRow {
  token: string;
  count: number;
  accountIds: string[];
}

export interface MissingRow {
  field: MissingField;
  count: number;
  accountIds: string[];
}

export interface ActivityRow extends AggregateSlice {
  bucket: ActivityBucket;
  label: string;
}

export interface DashboardAggregates {
  kpis: DashboardKpis;
  funnel: FunnelStage[];
  leaderboard: OwnerRow[];
  healthBuckets: HealthBucket[];
  topBlockers: BlockerRow[];
  missingMatrix: MissingRow[];
  activity: ActivityRow[];
}

function normalizeBlockerToken(raw: string): string {
  return raw.trim().toLowerCase().slice(0, 60);
}

function activityBucketFor(a: PipelineAccount): ActivityBucket {
  const d = daysSince(a.lastActivity);
  if (d === Infinity) return 'never';
  if (d <= 7) return 'active';
  if (d <= 21) return 'cooling';
  return 'stalled';
}

export function buildDashboardAggregates(
  accounts: PipelineAccount[],
): DashboardAggregates {
  const allAccountIds = accounts.map((a) => a.id);
  const atRiskAccountIds = accounts
    .filter((a) => a.hasAnalysis && computeHealth(a) < 50)
    .map((a) => a.id);
  const stalledAccountIds = accounts.filter((a) => isStalled(a)).map((a) => a.id);

  const kpis: DashboardKpis = {
    totalDeals: accounts.length,
    totalArr: accounts.reduce((s, a) => s + (a.arr ?? 0), 0),
    atRiskCount: atRiskAccountIds.length,
    stalledCount: stalledAccountIds.length,
    weightedArr: accounts.reduce(
      (s, a) => s + (a.arr ?? 0) * STAGE_WEIGHT[a.stage],
      0,
    ),
    allAccountIds,
    atRiskAccountIds,
    stalledAccountIds,
  };

  const funnel: FunnelStage[] = STAGE_ORDER.map((stage) => {
    const rows = accounts.filter((a) => a.stage === stage);
    return {
      stage,
      count: rows.length,
      totalArr: rows.reduce((s, a) => s + (a.arr ?? 0), 0),
      accountIds: rows.map((a) => a.id),
    };
  });

  const ownerMap = new Map<string, OwnerRow>();
  for (const a of accounts) {
    const ownerKey = a.owner.sfUserId ?? a.owner.email ?? a.owner.name;
    const cur = ownerMap.get(ownerKey);
    const health = a.hasAnalysis ? computeHealth(a) : 0;
    const atRisk = a.hasAnalysis && health < 50 ? 1 : 0;
    if (cur) {
      cur.count += 1;
      cur.totalArr += a.arr ?? 0;
      cur.avgHealth += health;
      cur.atRisk += atRisk;
      cur.accountIds.push(a.id);
    } else {
      ownerMap.set(ownerKey, {
        ownerKey,
        name: a.owner.name,
        initials: a.owner.initials,
        color: a.owner.color,
        email: a.owner.email ?? null,
        count: 1,
        totalArr: a.arr ?? 0,
        avgHealth: health,
        atRisk,
        accountIds: [a.id],
      });
    }
  }
  const leaderboard = Array.from(ownerMap.values())
    .map((o) => ({
      ...o,
      avgHealth: o.count > 0 ? Math.round(o.avgHealth / o.count) : 0,
    }))
    .sort((a, b) => b.totalArr - a.totalArr);

  const healthBucketDefs: Array<{
    band: HealthBucketKey;
    min: number;
    max: number;
  }> = [
    { band: 'at-risk', min: 0, max: 49 },
    { band: 'watch', min: 50, max: 74 },
    { band: 'healthy', min: 75, max: 100 },
  ];
  const healthBuckets: HealthBucket[] = healthBucketDefs.map(({ band, min, max }) => {
    const rows = accounts.filter((a) => {
      if (!a.hasAnalysis) return false;
      const score = computeHealth(a);
      return score >= min && score <= max;
    });
    const meta = healthBand(min === 0 ? 25 : min === 50 ? 60 : 80);
    return {
      band,
      label: meta.label,
      color: meta.color,
      bg: meta.bg,
      count: rows.length,
      totalArr: rows.reduce((s, a) => s + (a.arr ?? 0), 0),
      accountIds: rows.map((a) => a.id),
    };
  });

  const blockerMap = new Map<string, Set<string>>();
  for (const a of accounts) {
    for (const b of a.blockers ?? []) {
      const token = normalizeBlockerToken(b);
      if (!token) continue;
      if (!blockerMap.has(token)) blockerMap.set(token, new Set());
      blockerMap.get(token)!.add(a.id);
    }
  }
  const topBlockers: BlockerRow[] = Array.from(blockerMap.entries())
    .map(([token, ids]) => ({
      token,
      count: ids.size,
      accountIds: Array.from(ids),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const missingMap = new Map<MissingField, Set<string>>();
  for (const field of MISSING_FIELD_ORDER) {
    missingMap.set(field, new Set());
  }
  for (const a of accounts) {
    for (const field of missingFields(a)) {
      missingMap.get(field)!.add(a.id);
    }
  }
  const missingMatrix: MissingRow[] = MISSING_FIELD_ORDER.map((field) => {
    const ids = Array.from(missingMap.get(field)!);
    return { field, count: ids.length, accountIds: ids };
  });

  const activityDefs: Array<{ bucket: ActivityBucket; label: string }> = [
    { bucket: 'active', label: 'Active (≤7d)' },
    { bucket: 'cooling', label: 'Cooling (8–21d)' },
    { bucket: 'stalled', label: 'Stalled (>21d)' },
    { bucket: 'never', label: 'No activity' },
  ];
  const activity: ActivityRow[] = activityDefs.map(({ bucket, label }) => {
    const rows = accounts.filter((a) => activityBucketFor(a) === bucket);
    return {
      bucket,
      label,
      count: rows.length,
      totalArr: rows.reduce((s, a) => s + (a.arr ?? 0), 0),
      accountIds: rows.map((a) => a.id),
    };
  });

  return {
    kpis,
    funnel,
    leaderboard,
    healthBuckets,
    topBlockers,
    missingMatrix,
    activity,
  };
}
